'use client';
import { IMessage } from './message.typings';
import type { IConversationSet } from '@ui/components/organisms/conversation/conversation.typings';

/**
 * Personalizes system message text based on the viewer's perspective.
 *
 * @param message - The system message object
 * @param currentUserId - The ID of the current user viewing the message
 * @returns Personalized message text or the default description
 *
 * Examples:
 * - If current user added others: "You added Ada and Chike"
 * - If current user was added: "You have been added to the group"
 * - Otherwise: Uses the default backend description
 */
export const getPersonalizedSystemMessage = (
  message: IMessage,
  currentUserId?: string | null,
): string => {
  const misc = message.miscellaneous;

  // Only personalize "member_added" system messages
  if (!misc?.system || misc.system_type !== 'member_added') {
    return message.description || '';
  }

  const actorId = misc.actor_id;
  const addedUserIds = misc.added_user_ids || [];
  const addedUserNames = misc.added_user_display_names || [];

  // Case 1: Current user is the one who added users
  if (currentUserId && actorId && String(currentUserId) === String(actorId)) {
    if (addedUserNames.length === 0) {
      return 'You added new members';
    } else if (addedUserNames.length === 1) {
      return `You added ${addedUserNames[0]}`;
    } else if (addedUserNames.length === 2) {
      return `You added ${addedUserNames[0]} and ${addedUserNames[1]}`;
    } else {
      const firstTwo = addedUserNames.slice(0, 2);
      const remainingCount = addedUserNames.length - 2;
      return `You added ${firstTwo.join(', ')} and ${remainingCount} other${
        remainingCount > 1 ? 's' : ''
      }`;
    }
  }

  // Case 2: Current user is among those added
  if (currentUserId && addedUserIds.some((id: string) => String(id) === String(currentUserId))) {
    const otherAddedUsers = addedUserNames.filter(
      (_: string, idx: number) => String(addedUserIds[idx]) !== String(currentUserId),
    );

    if (otherAddedUsers.length === 0) {
      return 'You have been added to the group';
    } else if (otherAddedUsers.length === 1) {
      return `You and ${otherAddedUsers[0]} have been added to the group`;
    } else if (otherAddedUsers.length === 2) {
      return `You, ${otherAddedUsers[0]}, and ${otherAddedUsers[1]} have been added to the group`;
    } else {
      const firstTwo = otherAddedUsers.slice(0, 2);
      const remainingCount = otherAddedUsers.length - 2;
      return `You, ${firstTwo.join(', ')}, and ${remainingCount} other${
        remainingCount > 1 ? 's' : ''
      } have been added to the group`;
    }
  }

  // Case 3: Default - use backend description
  return message.description || '';
};

/**
 * Formats the conversation preview text for display in conversation lists.
 * For system messages, returns personalized text without sender name prefix.
 * For regular messages, returns formatted text with sender name.
 *
 * @param conversationSet - The conversation set containing last message info
 * @param currentUserId - The ID of the current user viewing the conversation
 * @param currentUsername - The username of the current user (optional, for "You" replacement)
 * @returns Object with `text` (the preview text) and `showSender` (whether to show sender name)
 *
 * Examples:
 * - System message: { text: "You added Ada and Chike", showSender: false }
 * - Regular message: { text: "Hello world", showSender: true }
 */
export const getConversationPreviewText = (
  conversationSet: IConversationSet,
  currentUserId?: string | null,
  currentUsername?: string | null,
): { text: string; showSender: boolean } => {
  const { text_of_last_message, user_name_last_message, last_message_miscellaneous } =
    conversationSet;

  if (!text_of_last_message) {
    return { text: '', showSender: false };
  }

  // Check if the last message is a system message
  const isSystemMessage = last_message_miscellaneous?.system === true;

  if (isSystemMessage) {
    // Create a mock message object to reuse the personalized system message logic
    const mockMessage: IMessage = {
      id: '',
      description: text_of_last_message,
      miscellaneous: last_message_miscellaneous,
    } as IMessage;

    const personalizedText = getPersonalizedSystemMessage(mockMessage, currentUserId);
    return { text: personalizedText, showSender: false };
  }

  // Regular message: return text with sender name prefix
  return { text: text_of_last_message, showSender: true };
};
